import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { inr, ymd } from "@/lib/format";

export default function Ledger() {
  const [params, setParams] = useSearchParams();
  const [parties, setParties] = useState([]);
  const [partyId, setPartyId] = useState(params.get("party_id") || "");
  const [ledger, setLedger] = useState(null);

  useEffect(() => {
    api.get("/parties").then(({ data }) => setParties(data));
  }, []);

  useEffect(() => {
    if (!partyId) return setLedger(null);
    setParams({ party_id: partyId });
    api.get(`/ledger/${partyId}`).then(({ data }) => setLedger(data));
  }, [partyId]);

  const kindLabel = (k) => ({
    opening: "Opening", invoice: "Invoice", po: "Purchase Order",
    payment_in: "Payment Received", payment_out: "Payment Made"
  }[k] || k);

  return (
    <div>
      <PageHeader title="Ledger" subtitle="Books of account — per party statement" />
      <div className="px-8 py-6 space-y-6">
        <Card className="p-6 border-slate-200">
          <div className="max-w-sm">
            <Select value={partyId} onValueChange={setPartyId}>
              <SelectTrigger data-testid="ledger-party-select"><SelectValue placeholder="Select a party" /></SelectTrigger>
              <SelectContent>
                {parties.map((p) => (<SelectItem key={p.party_id} value={p.party_id}>{p.name} — {p.kind}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </Card>

        {ledger && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="p-5 border-slate-200">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Party</div>
                <div className="font-display text-xl font-bold mt-1">{ledger.party.name}</div>
                <div className="text-xs text-slate-500 mt-1 capitalize">{ledger.party.kind}</div>
              </Card>
              <Card className="p-5 border-slate-200">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Contact</div>
                <div className="text-sm mt-1">{ledger.party.email || "—"}</div>
                <div className="text-xs text-slate-500">{ledger.party.phone || ""}</div>
              </Card>
              <Card className="p-5 border-slate-200">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Closing Balance</div>
                <div className={`font-display text-2xl font-black mt-1 number-cell ${ledger.balance > 0 ? "text-rose-600" : ledger.balance < 0 ? "text-emerald-600" : ""}`}>
                  {inr(Math.abs(ledger.balance))} {ledger.balance > 0 ? "Dr" : ledger.balance < 0 ? "Cr" : ""}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {ledger.balance > 0
                    ? (ledger.party.kind === "customer" ? "Owes us" : "We owe them")
                    : ledger.balance < 0
                      ? "Advance / credit"
                      : "Settled"}
                </div>
              </Card>
            </div>

            <Card className="border-slate-200 overflow-hidden">
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead className="text-right">Debit</TableHead>
                    <TableHead className="text-right">Credit</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ledger.entries.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-500">No transactions yet.</TableCell></TableRow>
                  )}
                  {ledger.entries.map((e, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-slate-500">{ymd(e.date)}</TableCell>
                      <TableCell>{kindLabel(e.kind)}</TableCell>
                      <TableCell>
                        {e.doc_id ? (
                          <Link to={`/documents/${e.doc_id}`} className="text-indigo-700 hover:underline">{e.reference}</Link>
                        ) : e.reference}
                      </TableCell>
                      <TableCell className="text-right number-cell">{e.debit ? inr(e.debit) : "—"}</TableCell>
                      <TableCell className="text-right number-cell">{e.credit ? inr(e.credit) : "—"}</TableCell>
                      <TableCell className="text-right number-cell font-semibold">{inr(Math.abs(e.balance))} {e.balance > 0 ? "Dr" : e.balance < 0 ? "Cr" : ""}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
